import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import axios from 'axios';
import './App.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Main Website Component (preserving original design)
const MondayKnightsWebsite = () => {
  const [activeOverlay, setActiveOverlay] = useState(null);
  const [contactStep, setContactStep] = useState('menu'); // 'menu' or 'forms'
  const [aboutContent, setAboutContent] = useState("Action Aid Community\n\nMonday Knights is an affinity group based on the premise that Revolutionary work must be made available to everyone. There are a multitude of avenues to pursue in our fight for liberation, and we are committed to highlighting and supporting them.\n\nIn this capitalist society, each one of us is faced with obstacles that dictate our capacity to participate. Be they physical limitations, personal obligations or time constraints, the concept of engaging in society challenging struggles can seem intimidating. We exist to bridge the gap between revolutionary enthusiasm and meaningful action.\n\nOur philosophy is simple: Anyone can contribute one day a month to the betterment of society. You tell us what day works for you, and we will plug you in!");
  const [userType, setUserType] = useState('');
  const [showPrivacyPolicy, setShowPrivacyPolicy] = useState(false);
  const [formData, setFormData] = useState({});
  const [blogPosts, setBlogPosts] = useState([]);
  const [individualContacts, setIndividualContacts] = useState([]);
  const [businessContacts, setBusinessContacts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');

  useEffect(() => {
    fetchAboutContent();
    fetchBlogPosts();
    fetchContacts();
  }, []);

  const fetchAboutContent = async () => {
    try {
      const response = await axios.get(`${API}/about-us`);
      if (response.data.content) {
        setAboutContent(response.data.content);
      }
    } catch (error) {
      console.error('Error fetching about content:', error);
    }
  };

  const getAuthHeaders = () => ({
    headers: { 'Authorization': `Bearer ${localStorage.getItem('adminToken')}` }
  });

  const fetchBlogPosts = async () => {
    try {
      const response = await axios.get(`${API}/blog-posts`);
      setBlogPosts(response.data);
    } catch (error) {
      console.error('Error fetching blog posts:', error);
    }
  };

  const fetchContacts = async () => {
    try {
      const [individualResponse, businessResponse] = await Promise.all([
        axios.get(`${API}/contact/individual`, getAuthHeaders()),
        axios.get(`${API}/contact/business`, getAuthHeaders())
      ]);
      setIndividualContacts(individualResponse.data);
      setBusinessContacts(businessResponse.data);
    } catch (error) {
      console.error('Error fetching contacts:', error);
    }
  };

  const showOverlay = (overlayName) => {
    setActiveOverlay(overlayName);
  };

  const closeOverlay = () => {
    setActiveOverlay(null);
    setContactStep('menu'); // Reset to menu when closing
    setUserType('');
    setFormData({});
    setMessage('');
  };

  const showDynamicForm = (type) => {
    setUserType(type);
    setFormData({ privacyAgreed: true }); // Pre-check privacy agreement
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const formatPhoneNumber = (value) => {
    let cleaned = value.replace(/\D/g, '');
    if (cleaned.length >= 6) {
      return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6, 10)}`;
    } else if (cleaned.length >= 3) {
      return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3)}`;
    }
    return cleaned;
  };

  const saveForm = async () => {
    setLoading(true);
    setMessage('');

    // Check if privacy policy is agreed
    if (!formData.privacyAgreed) {
      setMessage('Please agree to the privacy policy.');
      setLoading(false);
      return;
    }

    try {
      let submitData;
      let endpoint;

      if (userType === 'individual') {
        // Validate required fields
        if (!formData.name || !formData.email) {
          setMessage('Please fill in all mandatory fields.');
          setLoading(false);
          return;
        }

        submitData = {
          name: `${formData.name} ${formData.lastName || ''}`.trim(),
          email: formData.email,
          phone: formData.phone || '',
          message: formData.message || 'Contact form submission',
          privacy_agreed: formData.privacyAgreed
        };
        endpoint = 'contact/individual';
      } else if (userType === 'business') {
        // Validate required fields
        if (!formData.name || !formData.businessName || !formData.businessEmail) {
          setMessage('Please fill in all mandatory fields.');
          setLoading(false);
          return;
        }

        submitData = {
          company_name: formData.businessName,
          contact_person: `${formData.name} ${formData.lastName || ''}`.trim(),
          email: formData.businessEmail,
          phone: formData.businessPhone || '',
          message: formData.message || 'Business contact form submission',
          privacy_agreed: formData.privacyAgreed
        };
        endpoint = 'contact/business';
      }

      await axios.post(`${API}/${endpoint}`, submitData);
      setMessage('Form saved successfully!');
      setTimeout(() => {
        closeOverlay();
      }, 2000);
    } catch (error) {
      setMessage('Error saving form. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  return (
    <div className="app-container">
      {/* Updated Background Image */}
      <img 
        id="backgroundImage" 
        src="https://customer-assets.emergentagent.com/job_event-portal-8/artifacts/a8xh0uxg_New%20Background.png" 
        alt="Background" 
        style={{ display: 'block' }}
      />
      
      {/* Menu Container - preserving original positioning */}
      <div className="menu-container">
        <div className="menu-item" onClick={() => showOverlay('about')}>About Us</div>
        <div className="menu-item" onClick={() => showOverlay('contact')}>Contact Us</div>
        <div className="menu-item" onClick={() => showOverlay('events')}>Events and Announcements</div>
      </div>

      {/* About Us Overlay - preserving original content */}
      {activeOverlay === 'about' && (
        <div className="overlay" onClick={(e) => e.target.className === 'overlay' && closeOverlay()}>
          <div className="overlay-content">
            <button className="close-btn" onClick={closeOverlay}>&times;</button>
            <div className="about-text">
              {aboutContent}
            </div>
          </div>
        </div>
      )}

      {/* Contact Us Overlay - New Sub-menu Structure */}
      {activeOverlay === 'contact' && (
        <div className="overlay" onClick={(e) => e.target.className === 'overlay' && closeOverlay()}>
          <div className="overlay-content">
            <button className="close-btn" onClick={closeOverlay}>&times;</button>
            
            {message && (
              <div className={`message ${message.includes('Error') ? 'error' : 'success'}`}>
                {message}
              </div>
            )}

            {/* Main Contact Menu */}
            {contactStep === 'menu' && (
              <div style={{ textAlign: 'center', padding: '2rem 1rem' }}>
                <h3 style={{ color: '#ca6ce6', marginBottom: '2rem', fontSize: '1.8rem' }}>
                  Contact Monday Knights
                </h3>
                
                {/* Subscribe to Email List */}
                <div 
                  className="contact-menu-item"
                  onClick={() => setContactStep('forms')}
                  style={{
                    background: 'rgba(202, 108, 230, 0.1)',
                    border: '2px solid #ca6ce6',
                    borderRadius: '15px',
                    padding: '2rem',
                    marginBottom: '2rem',
                    cursor: 'pointer',
                    transition: 'all 0.3s ease',
                    ':hover': { transform: 'scale(1.02)' }
                  }}
                  onMouseEnter={(e) => e.target.style.transform = 'scale(1.02)'}
                  onMouseLeave={(e) => e.target.style.transform = 'scale(1)'}
                >
                  <h4 style={{ color: '#ffd700', marginBottom: '1rem', fontSize: '1.3rem' }}>
                    Subscribe to Email List
                  </h4>
                  <p style={{ color: '#e0e0e0', fontSize: '1rem' }}>
                    Join our community and stay updated with events and announcements
                  </p>
                </div>

                {/* Follow Us on Instagram */}
                <div style={{
                  background: 'rgba(255, 215, 0, 0.1)',
                  border: '2px solid #ffd700',
                  borderRadius: '15px',
                  padding: '2rem',
                  textAlign: 'center'
                }}>
                  <h4 style={{ color: '#ca6ce6', marginBottom: '1rem', fontSize: '1.3rem' }}>
                    Follow Us
                  </h4>
                  <p style={{ color: '#e0e0e0', marginBottom: '1.5rem' }}>
                    Stay connected on social media
                  </p>
                  
                  {/* Instagram Logo and Link */}
                  <a 
                    href="https://www.instagram.com/monday_knights_nyc/"
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      display: 'inline-block',
                      transition: 'transform 0.3s ease'
                    }}
                    onMouseEnter={(e) => e.target.style.transform = 'scale(1.1)'}
                    onMouseLeave={(e) => e.target.style.transform = 'scale(1)'}
                  >
                    <svg
                      width="60"
                      height="60"
                      viewBox="0 0 24 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                      style={{
                        background: 'linear-gradient(45deg, #f09433 0%,#e6683c 25%,#dc2743 50%,#cc2366 75%,#bc1888 100%)',
                        borderRadius: '12px',
                        padding: '8px'
                      }}
                    >
                      <path
                        d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"
                        fill="white"
                      />
                    </svg>
                  </a>
                  
                  <p style={{ 
                    color: '#ca6ce6', 
                    marginTop: '1rem', 
                    fontSize: '0.9rem',
                    fontWeight: '500'
                  }}>
                    @monday_knights_nyc
                  </p>
                </div>
              </div>
            )}

            {/* Email Subscription Forms (moved from main contact) */}
            {contactStep === 'forms' && (
              <div>
                <div style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  marginBottom: '2rem',
                  gap: '1rem'
                }}>
                  <button 
                    onClick={() => setContactStep('menu')}
                    style={{
                      background: 'none',
                      border: 'none',
                      color: '#ca6ce6',
                      fontSize: '1.5rem',
                      cursor: 'pointer',
                      padding: '0.5rem'
                    }}
                  >
                    ← Back
                  </button>
                  <h3 style={{ color: '#ca6ce6', margin: 0 }}>
                    Subscribe to Email List
                  </h3>
                </div>

                <form id="contactForm">
                  <div className="form-group">
                    <label className="form-label">Are you an individual or business?</label>
                    <select 
                      className="form-select" 
                      value={userType} 
                      onChange={(e) => showDynamicForm(e.target.value)}
                    >
                      <option value="">Select...</option>
                      <option value="individual">Individual</option>
                      <option value="business">Business</option>
                    </select>
                  </div>
                  
                  {/* Individual Form */}
                  {userType === 'individual' && (
                    <div className="dynamic-form">
                      <div className="form-group">
                        <label className="form-label">Name</label>
                        <input 
                          type="text" 
                          className="form-input" 
                          value={formData.name || ''} 
                          onChange={(e) => handleInputChange('name', e.target.value)}
                          required 
                        />
                      </div>
                      <div className="form-group">
                        <label className="form-label">Last Name</label>
                        <input 
                          type="text" 
                          className="form-input" 
                          value={formData.lastName || ''} 
                          onChange={(e) => handleInputChange('lastName', e.target.value)}
                        />
                      </div>
                      <div className="form-group">
                        <label className="form-label">Email</label>
                        <input 
                          type="email" 
                          className="form-input" 
                          value={formData.email || ''} 
                          onChange={(e) => handleInputChange('email', e.target.value)}
                          required 
                        />
                      </div>
                      <div className="form-group">
                        <label className="form-label">Phone</label>
                        <input 
                          type="tel" 
                          className="form-input" 
                          placeholder="(xxx) xxx-xxxx" 
                          value={formData.phone || ''} 
                          onChange={(e) => handleInputChange('phone', formatPhoneNumber(e.target.value))}
                        />
                      </div>
                      <div className="form-group">
                        <label className="form-label">Message (optional)</label>
                        <textarea 
                          className="form-input" 
                          rows="4"
                          value={formData.message || ''} 
                          onChange={(e) => handleInputChange('message', e.target.value)}
                          placeholder="Tell us how you'd like to get involved..."
                        />
                      </div>
                      
                      {/* Privacy Policy Checkbox */}
                      <div className="checkbox-container">
                        <input
                          type="checkbox"
                          id="individual-privacy"
                          checked={formData.privacyAgreed !== undefined ? formData.privacyAgreed : true}
                          onChange={(e) => handleInputChange('privacyAgreed', e.target.checked)}
                        />
                        <label htmlFor="individual-privacy" className="checkbox-label">
                          By checking this box, you agree to receive communications and share your information with Monday Knights. 
                          Please review our <span className="privacy-link" onClick={() => setShowPrivacyPolicy(true)}>Privacy Policy</span> for details.
                        </label>
                      </div>
                    </div>
                  )}

                  {/* Business Form */}
                  {userType === 'business' && (
                    <div className="dynamic-form">
                      <div className="form-group">
                        <label className="form-label">Name</label>
                        <input 
                          type="text" 
                          className="form-input" 
                          value={formData.name || ''} 
                          onChange={(e) => handleInputChange('name', e.target.value)}
                          required 
                        />
                      </div>
                      <div className="form-group">
                        <label className="form-label">Last Name</label>
                        <input 
                          type="text" 
                          className="form-input" 
                          value={formData.lastName || ''} 
                          onChange={(e) => handleInputChange('lastName', e.target.value)}
                        />
                      </div>
                      <div className="form-group">
                        <label className="form-label">Business Name</label>
                        <input 
                          type="text" 
                          className="form-input" 
                          value={formData.businessName || ''} 
                          onChange={(e) => handleInputChange('businessName', e.target.value)}
                          required 
                        />
                      </div>
                      <div className="form-group">
                        <label className="form-label">Business Contact Email</label>
                        <input 
                          type="email" 
                          className="form-input" 
                          value={formData.businessEmail || ''} 
                          onChange={(e) => handleInputChange('businessEmail', e.target.value)}
                          required 
                        />
                      </div>
                      <div className="form-group">
                        <label className="form-label">Business Phone Number</label>
                        <input 
                          type="tel" 
                          className="form-input" 
                          placeholder="(xxx) xxx-xxxx" 
                          value={formData.businessPhone || ''} 
                          onChange={(e) => handleInputChange('businessPhone', formatPhoneNumber(e.target.value))}
                        />
                      </div>
                      <div className="form-group">
                        <label className="form-label">Message (optional)</label>
                        <textarea 
                          className="form-input" 
                          rows="4"
                          value={formData.message || ''} 
                          onChange={(e) => handleInputChange('message', e.target.value)}
                          placeholder="Tell us about your business and how we can work together..."
                        />
                      </div>
                      
                      {/* Privacy Policy Checkbox */}
                      <div className="checkbox-container">
                        <input
                          type="checkbox"
                          id="business-privacy"
                          checked={formData.privacyAgreed !== undefined ? formData.privacyAgreed : true}
                          onChange={(e) => handleInputChange('privacyAgreed', e.target.checked)}
                        />
                        <label htmlFor="business-privacy" className="checkbox-label">
                          By checking this box, you agree to receive communications and share your information with Monday Knights. 
                          Please review our <span className="privacy-link" onClick={() => setShowPrivacyPolicy(true)}>Privacy Policy</span> for details.
                        </label>
                      </div>
                    </div>
                  )}
                  
                  {userType && (
                    <button 
                      type="button" 
                      className={`save-btn ${loading ? 'loading' : ''}`}
                      onClick={saveForm}
                      disabled={loading}
                    >
                      {loading ? 'Subscribing...' : 'Subscribe'}
                    </button>
                  )}
                </form>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Events and Announcements Overlay */}
      {activeOverlay === 'events' && (
        <div className="overlay" onClick={(e) => e.target.className === 'overlay' && closeOverlay()}>
          <div className="overlay-content events-content">
            <button className="close-btn" onClick={closeOverlay}>&times;</button>
            
            {/* Monday Knights Header Image */}
            <div className="events-header">
              <img 
                src="https://customer-assets.emergentagent.com/job_event-portal-8/artifacts/wk8rqmze_events%20header.png" 
                alt="Monday Knights Header" 
                className="events-header-img"
              />
            </div>
            
            <div className="about-text">
              {blogPosts.length === 0 ? (
                <p>No events or announcements yet. Check back soon for updates!</p>
              ) : (
                <div className="blog-posts">
                  {blogPosts.map((post) => (
                    <div key={post.id} className="blog-post">
                      {post.image_url && (
                        <img src={post.image_url} alt={post.title} className="blog-post-image" />
                      )}
                      <h4 className="blog-post-title">{post.title}</h4>
                      <p className="blog-post-date">{formatDate(post.created_at)}</p>
                      <div className="blog-post-content">{post.content}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Privacy Policy Modal */}
      {showPrivacyPolicy && (
        <div className="overlay" onClick={(e) => e.target.className === 'overlay' && setShowPrivacyPolicy(false)}>
          <div className="overlay-content privacy-content">
            <button className="close-btn" onClick={() => setShowPrivacyPolicy(false)}>&times;</button>
            <div className="about-text">
              <h3 style={{ color: '#ca6ce6', marginBottom: '20px' }}>Privacy Policy</h3>
              <div style={{ whiteSpace: 'pre-line', fontSize: '14px', lineHeight: '1.6' }}>
                {`Monday Knights Privacy Policy
Effective Date: 09/01/2025

Monday Knights ("we," "us," or "our") is committed to protecting your privacy. This Privacy Policy outlines how we collect, use, store, and protect your information.

What Information We Collect
We may collect the following types of information:

Personal Information: such as your name, email address, mailing address, phone number, and other details you provide when you contact us, donate, register for events, or sign up for newsletters.

Non-Personal Information: including your browser type, device, IP address, and how you use our website.

How We Use Your Information
We use your information solely to:

Communicate with you regarding our programs, events, and updates.

Process donations and provide donation receipts.

Respond to your inquiries.

Improve our services and website functionality.

No Sale or Marketing Use
We will never sell, rent, or trade your personal information.
Your information will not be used for marketing purposes or shared with third parties for advertising. Monday Knights only uses your information to fulfill our non-profit mission and communicate with you as described above.

Cookies and Tracking
Our website may use cookies for analytics and essential functionality. You may disable cookies through your browser settings.

Information Sharing
We may share your details only:

With service providers who process donations and communications on our behalf, under strict confidentiality agreements.

If required by law or legal process.

Data Security
We safeguard your information through physical, administrative, and technical measures and restrict access to authorized personnel only.

Children's Privacy
We do not knowingly collect personal data from children under 13.

Your Choices
You may:

Opt out of non-essential communications at any time.

Request access, correction, or deletion of your information by contacting us at federico.celina@gmail.com.

Policy Updates
We may update this Privacy Policy from time to time. Any changes will be posted at this website with a new effective date.

Contact Us
If you have questions about this policy, please contact us at:
federico.celina@gmail.com

Monday Knights values your trust. Your privacy is protected and respected.`}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Admin Link (hidden) */}
      <div className="admin-link">
        <a href="/admin" style={{ color: 'transparent', fontSize: '1px' }}>Admin</a>
      </div>
    </div>
  );
};

// Admin Components
const AdminLogin = ({ onLogin }) => {
  const [credentials, setCredentials] = useState({ email: '', password: '' });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const response = await axios.post(`${API}/admin/login`, credentials);
      onLogin(response.data.access_token);
    } catch (error) {
      setError(error.response?.data?.detail || 'Login failed. Please check your credentials.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="admin-container">
      <div className="admin-form">
        <h2>Admin Login</h2>
        {error && <div className="error-message">{error}</div>}
        <form onSubmit={handleSubmit}>
          <input
            type="email"
            placeholder="Email"
            value={credentials.email}
            onChange={(e) => setCredentials({...credentials, email: e.target.value})}
            required
          />
          <input
            type="password"
            placeholder="Password"
            value={credentials.password}
            onChange={(e) => setCredentials({...credentials, password: e.target.value})}
            required
          />
          <button type="submit" disabled={loading}>
            {loading ? 'Logging in...' : 'Login'}
          </button>
        </form>
        <p><a href="/">Back to Website</a></p>
      </div>
    </div>
  );
};

const AdminDashboard = ({ onLogout }) => {
  const [activeTab, setActiveTab] = useState('about');
  const [aboutContent, setAboutContent] = useState('');
  const [blogPosts, setBlogPosts] = useState([]);
  const [newPost, setNewPost] = useState({ title: '', content: '', image_url: '' });
  const [editingPost, setEditingPost] = useState(null);
  const [individualContacts, setIndividualContacts] = useState([]);
  const [businessContacts, setBusinessContacts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');

  useEffect(() => {
    fetchAboutContent();
    fetchBlogPosts();
    fetchContacts();
  }, []);

  const getAuthHeaders = () => ({
    headers: { 'Authorization': `Bearer ${localStorage.getItem('adminToken')}` }
  });

  const fetchAboutContent = async () => {
    try {
      const response = await axios.get(`${API}/about-us`);
      setAboutContent(response.data.content);
    } catch (error) {
      console.error('Error fetching about content:', error);
    }
  };

  const fetchBlogPosts = async () => {
    try {
      const response = await axios.get(`${API}/blog-posts`);
      setBlogPosts(response.data);
    } catch (error) {
      console.error('Error fetching blog posts:', error);
    }
  };

  const fetchContacts = async () => {
    try {
      const [individualResponse, businessResponse] = await Promise.all([
        axios.get(`${API}/contact/individual`, getAuthHeaders()),
        axios.get(`${API}/contact/business`, getAuthHeaders())
      ]);
      setIndividualContacts(individualResponse.data);
      setBusinessContacts(businessResponse.data);
    } catch (error) {
      console.error('Error fetching contacts:', error);
    }
  };

  const updateAboutContent = async () => {
    setLoading(true);
    try {
      await axios.put(`${API}/about-us`, { content: aboutContent }, getAuthHeaders());
      setMessage('About Us content updated successfully!');
    } catch (error) {
      setMessage('Error updating About Us content.');
    } finally {
      setLoading(false);
    }
  };

  const createBlogPost = async () => {
    setLoading(true);
    try {
      await axios.post(`${API}/blog-posts`, newPost, getAuthHeaders());
      setNewPost({ title: '', content: '', image_url: '' });
      fetchBlogPosts();
      setMessage('Blog post created successfully!');
    } catch (error) {
      setMessage('Error creating blog post.');
    } finally {
      setLoading(false);
    }
  };

  const editBlogPost = (post) => {
    setEditingPost(post);
    setNewPost({
      title: post.title,
      content: post.content,
      image_url: post.image_url || ''
    });
  };

  const updateBlogPost = async () => {
    setLoading(true);
    try {
      await axios.put(`${API}/blog-posts/${editingPost.id}`, newPost, getAuthHeaders());
      setNewPost({ title: '', content: '', image_url: '' });
      setEditingPost(null);
      fetchBlogPosts();
      setMessage('Blog post updated successfully!');
    } catch (error) {
      setMessage('Error updating blog post.');
    } finally {
      setLoading(false);
    }
  };

  const cancelEdit = () => {
    setEditingPost(null);
    setNewPost({ title: '', content: '', image_url: '' });
    setMessage('');
  };

  const deleteBlogPost = async (postId) => {
    if (!window.confirm('Are you sure you want to delete this post?')) return;
    
    try {
      await axios.delete(`${API}/blog-posts/${postId}`, getAuthHeaders());
      fetchBlogPosts();
      setMessage('Blog post deleted successfully!');
    } catch (error) {
      setMessage('Error deleting blog post.');
    }
  };

  return (
    <div className="admin-container">
      <div className="admin-dashboard">
        <div className="admin-header">
          <h2>Monday Knights Admin Dashboard</h2>
          <button onClick={onLogout} className="logout-btn">Logout</button>
        </div>

        <div className="admin-tabs">
          <button 
            className={activeTab === 'about' ? 'active' : ''} 
            onClick={() => setActiveTab('about')}
          >
            About Us
          </button>
          <button 
            className={activeTab === 'events' ? 'active' : ''} 
            onClick={() => setActiveTab('events')}
          >
            Events & Posts
          </button>
          <button 
            className={activeTab === 'contacts' ? 'active' : ''} 
            onClick={() => setActiveTab('contacts')}
          >
            Contact Submissions
          </button>
        </div>

        {message && <div className="admin-message">{message}</div>}

        {activeTab === 'about' && (
          <div className="tab-content">
            <h3>Update About Us Content</h3>
            <textarea
              value={aboutContent}
              onChange={(e) => setAboutContent(e.target.value)}
              rows="10"
              placeholder="Enter About Us content..."
            />
            <button onClick={updateAboutContent} disabled={loading}>
              {loading ? 'Updating...' : 'Update About Us'}
            </button>
          </div>
        )}

        {activeTab === 'events' && (
          <div className="tab-content">
            <h3>{editingPost ? 'Edit Post' : 'Create New Post'}</h3>
            <input
              type="text"
              placeholder="Post Title"
              value={newPost.title}
              onChange={(e) => setNewPost({...newPost, title: e.target.value})}
            />
            <input
              type="url"
              placeholder="Image URL (optional)"
              value={newPost.image_url}
              onChange={(e) => setNewPost({...newPost, image_url: e.target.value})}
            />
            <textarea
              placeholder="Post Content"
              value={newPost.content}
              onChange={(e) => setNewPost({...newPost, content: e.target.value})}
              rows="6"
            />
            <div style={{ display: 'flex', gap: '10px' }}>
              <button onClick={editingPost ? updateBlogPost : createBlogPost} disabled={loading}>
                {loading ? (editingPost ? 'Updating...' : 'Creating...') : (editingPost ? 'Update Post' : 'Create Post')}
              </button>
              {editingPost && (
                <button onClick={cancelEdit} disabled={loading} style={{ background: '#666' }}>
                  Cancel
                </button>
              )}
            </div>

            <h3>Existing Posts</h3>
            <div className="blog-posts-admin">
              {blogPosts.map((post) => (
                <div key={post.id} className="blog-post-admin">
                  <div>
                    <h4>{post.title}</h4>
                    <p>{post.content.substring(0, 100)}...</p>
                  </div>
                  <div style={{ display: 'flex', gap: '10px' }}>
                    <button 
                      onClick={() => editBlogPost(post)}
                      style={{ 
                        background: '#ca6ce6', 
                        color: 'white',
                        border: 'none',
                        padding: '8px 15px',
                        borderRadius: '3px',
                        cursor: 'pointer',
                        fontSize: '12px'
                      }}
                    >
                      Edit
                    </button>
                    <button onClick={() => deleteBlogPost(post.id)}>Delete</button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {activeTab === 'contacts' && (
          <div className="tab-content">
            <h3>Contact Form Submissions</h3>
            
            <div style={{ marginBottom: '2rem' }}>
              <h4 style={{ color: '#ca6ce6', marginBottom: '1rem' }}>
                Individual Contacts ({individualContacts.length})
              </h4>
              {individualContacts.length === 0 ? (
                <p style={{ color: '#ccc', fontStyle: 'italic' }}>No individual submissions yet.</p>
              ) : (
                <div style={{ maxHeight: '300px', overflowY: 'auto', marginBottom: '2rem' }}>
                  {individualContacts.map((contact) => (
                    <div key={contact.id} style={{
                      background: 'rgba(202, 108, 230, 0.1)',
                      border: '1px solid rgba(202, 108, 230, 0.3)',
                      borderRadius: '8px',
                      padding: '1rem',
                      marginBottom: '1rem'
                    }}>
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '0.5rem', marginBottom: '0.5rem' }}>
                        <div><strong>Name:</strong> {contact.name}</div>
                        <div><strong>Email:</strong> <a href={`mailto:${contact.email}`} style={{ color: '#ca6ce6' }}>{contact.email}</a></div>
                        <div><strong>Phone:</strong> {contact.phone}</div>
                        <div><strong>Date:</strong> {new Date(contact.created_at).toLocaleDateString()}</div>
                      </div>
                      <div><strong>Message:</strong></div>
                      <div style={{ background: 'rgba(0,0,0,0.3)', padding: '0.5rem', borderRadius: '4px', marginTop: '0.5rem' }}>
                        {contact.message}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div>
              <h4 style={{ color: '#ca6ce6', marginBottom: '1rem' }}>
                Business Contacts ({businessContacts.length})
              </h4>
              {businessContacts.length === 0 ? (
                <p style={{ color: '#ccc', fontStyle: 'italic' }}>No business submissions yet.</p>
              ) : (
                <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
                  {businessContacts.map((contact) => (
                    <div key={contact.id} style={{
                      background: 'rgba(255, 215, 0, 0.1)',
                      border: '1px solid rgba(255, 215, 0, 0.3)',
                      borderRadius: '8px',
                      padding: '1rem',
                      marginBottom: '1rem'
                    }}>
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '0.5rem', marginBottom: '0.5rem' }}>
                        <div><strong>Company:</strong> {contact.company_name}</div>
                        <div><strong>Contact:</strong> {contact.contact_person}</div>
                        <div><strong>Email:</strong> <a href={`mailto:${contact.email}`} style={{ color: '#ca6ce6' }}>{contact.email}</a></div>
                        <div><strong>Phone:</strong> {contact.phone}</div>
                        <div><strong>Date:</strong> {new Date(contact.created_at).toLocaleDateString()}</div>
                      </div>
                      <div><strong>Message:</strong></div>
                      <div style={{ background: 'rgba(0,0,0,0.3)', padding: '0.5rem', borderRadius: '4px', marginTop: '0.5rem' }}>
                        {contact.message}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <button 
              onClick={fetchContacts} 
              style={{ 
                marginTop: '1rem',
                background: '#ca6ce6',
                color: 'white',
                border: 'none',
                padding: '10px 20px',
                borderRadius: '5px',
                cursor: 'pointer'
              }}
            >
              Refresh Contacts
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

// Main App Component
function App() {
  const [isAdminLoggedIn, setIsAdminLoggedIn] = useState(false);
  const [currentPath, setCurrentPath] = useState('/');

  useEffect(() => {
    const adminToken = localStorage.getItem('adminToken');
    if (adminToken) {
      setIsAdminLoggedIn(true);
    }

    // Simple routing
    const path = window.location.pathname;
    setCurrentPath(path);
  }, []);

  const handleAdminLogin = (token) => {
    localStorage.setItem('adminToken', token);
    setIsAdminLoggedIn(true);
    setCurrentPath('/admin/dashboard');
    window.history.pushState({}, '', '/admin/dashboard');
  };

  const handleAdminLogout = () => {
    localStorage.removeItem('adminToken');
    setIsAdminLoggedIn(false);
    setCurrentPath('/');
    window.history.pushState({}, '', '/');
  };

  // Simple routing without React Router to preserve original structure
  if (currentPath === '/admin' || currentPath === '/admin/login') {
    if (isAdminLoggedIn) {
      return <AdminDashboard onLogout={handleAdminLogout} />;
    } else {
      return <AdminLogin onLogin={handleAdminLogin} />;
    }
  }

  if (currentPath === '/admin/dashboard') {
    if (isAdminLoggedIn) {
      return <AdminDashboard onLogout={handleAdminLogout} />;
    } else {
      return <AdminLogin onLogin={handleAdminLogin} />;
    }
  }

  return <MondayKnightsWebsite />;
}

export default App;